import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { LapData } from "../types";

interface Props {
  laps: LapData[];
  bestLapTime?: number | null;
  height?: number;
}

// Concrete values (canvas can't resolve CSS vars). Mirrors the design tokens.
const LINE = "#5b8def";        // --live (blue)
const AXIS = "#8d94a3";        // --text-secondary
const GRID = "rgba(141, 148, 163, 0.12)";
const TICK_FONT = '500 11px "Geist", system-ui, sans-serif';
const LABEL_FONT = '600 12px "Geist", system-ui, sans-serif';
const STATE_COLOR: Record<string, string> = {
  valid: "#4ade80",   // --flag-green
  invalid: "#facc15", // --flag-yellow
  pit: "#60a5fa",     // --flag-blue
  caution: "#6b7280", // grey
};

type LapState = keyof typeof STATE_COLOR;
function lapState(l: LapData): LapState {
  if (l.pitIn || l.pitOut) return "pit";
  if (!l.isGreen) return "caution";
  if (!l.isValid) return "invalid";
  return "valid";
}

function fmtClock(v: number, decimals: number): string {
  const m = Math.floor(v / 60);
  const s = (v % 60).toFixed(decimals);
  return `${m}:${s.padStart(decimals > 0 ? 3 + decimals : 2, "0")}`;
}

/**
 * Lap Times & Pacing — one lap-time-per-lap series. The y-axis ranges on the
 * racing laps only (green + valid + non-pit) so an out-lap can't squash the
 * pace line; out-of-range laps still plot (the line clips to the top edge).
 * No follow-line cursor — hovering a lap highlights its dot and shows a
 * floating tooltip with the lap time and delta vs best.
 */
export default function LapTimeChart({ laps, bestLapTime, height = 300 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const plotted = laps.filter((l) => l.lapTime && l.lapTime > 0);
    if (plotted.length === 0) return;

    const xData = plotted.map((l) => l.lapNum);
    const yData = plotted.map((l) => l.lapTime as number);
    const states = plotted.map(lapState);
    const best = bestLapTime ?? Math.min(...yData);

    // Range on racing laps; fall back so the chart never blanks.
    let racing = plotted.filter((l) => l.isGreen && l.isValid && !l.pitIn && !l.pitOut).map((l) => l.lapTime as number);
    if (racing.length < 2) racing = plotted.filter((l) => l.isValid).map((l) => l.lapTime as number);
    if (racing.length < 2) racing = yData;
    const spread = Math.max(Math.max(...racing) - Math.min(...racing), 0.5);
    const pad = spread * 0.1 + 0.15;
    const lo = Math.floor((Math.min(...racing) - pad) * 2) / 2;
    const hi = Math.ceil((Math.max(...racing) + pad) * 2) / 2;

    let tip: HTMLDivElement | null = null;

    const opts: uPlot.Options = {
      width: containerRef.current.clientWidth || 800,
      height,
      padding: [16, 12, 0, 12],
      // No crosshair lines; keep the point highlight on hover (uPlot's default
      // points factory). Do NOT set points.show:true — uPlot calls it as the
      // element factory, so a bool returns `true` and addClass(true,…) throws.
      cursor: { x: false, y: false },
      scales: { x: { time: false }, y: { range: () => [lo, hi] } },
      hooks: {
        init: [
          (u) => {
            tip = document.createElement("div");
            tip.className = "xp-tip";
            tip.style.display = "none";
            u.over.appendChild(tip);
          },
        ],
        setCursor: [
          (u) => {
            if (!tip) return;
            const idx = u.cursor.idx;
            if (idx == null || u.cursor.left == null || u.cursor.left < 0) {
              tip.style.display = "none";
              return;
            }
            const lap = xData[idx];
            const t = yData[idx];
            const delta = t > best ? `+${(t - best).toFixed(3)}` : "best";
            // L4: build the tooltip with textContent (no innerHTML) so it can
            // never become an XSS sink if it's ever reused for a user-supplied
            // label. Values are numeric today; this keeps it safe by construction.
            const mk = (cls: string, text: string) => {
              const s = document.createElement("span");
              s.className = cls;
              s.textContent = text;
              return s;
            };
            const deltaEl = mk("xp-tip-delta", delta);
            deltaEl.style.color = t > best ? "var(--text-secondary)" : "#4ade80";
            tip.replaceChildren(mk("xp-tip-lap", `LAP ${lap}`), mk("xp-tip-time", fmtClock(t, 3)), deltaEl);
            tip.style.left = `${u.valToPos(lap, "x", false)}px`;
            tip.style.top = `${u.valToPos(t, "y", false)}px`;
            tip.style.display = "block";
          },
        ],
        // Per-lap state dots painted over the line; best lap gets a white ring.
        draw: [
          (u) => {
            const ctx = u.ctx;
            ctx.save();
            for (let i = 0; i < xData.length; i++) {
              const xp = u.valToPos(xData[i], "x", true);
              const yp = u.valToPos(yData[i], "y", true);
              if (yp < u.bbox.top - 1 || yp > u.bbox.top + u.bbox.height + 1) continue;
              const isBest = Math.abs(yData[i] - best) < 1e-6;
              ctx.beginPath();
              ctx.arc(xp, yp, isBest ? 5 : 3.4, 0, Math.PI * 2);
              ctx.fillStyle = STATE_COLOR[states[i]];
              ctx.fill();
              if (isBest) {
                ctx.lineWidth = 2;
                ctx.strokeStyle = "#ffffff";
                ctx.stroke();
              }
            }
            ctx.restore();
          },
        ],
      },
      axes: [
        {
          stroke: AXIS,
          grid: { stroke: GRID },
          ticks: { stroke: GRID },
          font: TICK_FONT,
          labelFont: LABEL_FONT,
          label: "Lap",
          space: 24,
          splits: (_u, _ai, min, max) => {
            const out: number[] = [];
            for (let n = Math.ceil(min); n <= Math.floor(max); n++) out.push(n);
            return out;
          },
        },
        {
          stroke: AXIS,
          grid: { stroke: GRID },
          ticks: { stroke: GRID },
          font: TICK_FONT,
          values: (_u: uPlot, vals: number[]) => vals.map((v) => fmtClock(v, 1)),
        },
      ],
      series: [
        {},
        { label: "Lap Time", stroke: LINE, width: 2, points: { show: false } },
      ],
      legend: { show: false },
    };

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new uPlot(opts, [xData, yData], containerRef.current);

    const onResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.setSize({ width: containerRef.current.clientWidth, height });
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [laps, bestLapTime, height]);

  if (laps.length === 0) {
    return <div className="text-muted text-sm">No lap data</div>;
  }

  return (
    <>
      <style>{`
        .xp-laptime .xp-tip {
          position: absolute;
          transform: translate(-50%, -135%);
          pointer-events: none;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
          padding: 6px 10px;
          background: rgba(10, 14, 22, 0.96);
          border: 1px solid var(--rule);
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(2, 6, 23, 0.5);
          white-space: nowrap;
        }
        .xp-laptime .xp-tip-lap { font-family: var(--font-body); font-size: 13px; font-weight: 700; letter-spacing: 0.08em; color: var(--text-secondary); }
        .xp-laptime .xp-tip-time { font-family: var(--font-mono); font-size: 14px; font-weight: 700; color: var(--text-strong); }
        .xp-laptime .xp-tip-delta { font-family: var(--font-mono); font-size: 13px; font-weight: 600; }
      `}</style>
      <div ref={containerRef} className="xp-laptime" style={{ width: "100%", position: "relative" }} />
    </>
  );
}
